class MiniCart extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(html => {
        document.getElementById('mini-cart').innerHTML =
          this.getSectionInnerHTML(html, '.shopify-section');

          document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      })
      .catch(e => {
        console.error(e);
      });
  }

  open() {
    const detailsElement = this.drawer.querySelector('details');
    if (detailsElement.hasAttribute('open')) {
      return;
    }

    this.drawer.openMenuDrawer();
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      if (document.getElementById(section.id)) {
        document.getElementById(section.id).innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    }));

    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }
}

customElements.define('mini-cart', MiniCart);

/* ==========================================================================
   <cart-countdown>
   Added 2026-08-29.

   Lives inside #mini-cart, whose innerHTML is replaced wholesale by
   CartItems.renderContents() on every cart mutation. The element is therefore
   destroyed and reconstructed constantly. Two consequences drive this design:

     1. The deadline is stored in sessionStorage, not on the instance, so the
        timer does not reset to full duration every time a shopper changes a
        quantity.
     2. disconnectedCallback MUST clear the interval. Without it each re-render
        leaks a timer and the page ends up ticking N times per second.
   ========================================================================== */

class CartCountdown extends HTMLElement {
  static STORAGE_KEY = 'purelyb:cart-countdown-deadline';

  connectedCallback() {
    this.output = this.querySelector('[data-countdown-time]');
    this.textEl = this.querySelector('.mini-cart__countdown-text');
    this.expiredText = this.dataset.expiredText || '';

    const minutes = parseInt(this.dataset.minutes, 10);
    this.durationMs = (Number.isFinite(minutes) && minutes > 0 ? minutes : 10) * 60 * 1000;

    this.deadline = this.resolveDeadline();
    this.tick();

    // Only run a timer if there is still time left to show.
    if (this.deadline > Date.now()) {
      this.interval = setInterval(() => this.tick(), 1000);
    }
  }

  disconnectedCallback() {
    clearInterval(this.interval);
    this.interval = null;
  }

  /**
   * Reuse an in-flight deadline for this session; otherwise start a new one.
   * sessionStorage throws in some private-browsing modes, so every access is
   * guarded and falls back to a non-persistent deadline.
   */
  resolveDeadline() {
    const fresh = Date.now() + this.durationMs;

    let stored = null;
    try {
      stored = sessionStorage.getItem(CartCountdown.STORAGE_KEY);
    } catch (e) {
      return fresh;
    }

    const parsed = parseInt(stored, 10);
    if (Number.isFinite(parsed)) return parsed;

    try {
      sessionStorage.setItem(CartCountdown.STORAGE_KEY, String(fresh));
    } catch (e) {
      /* storage unavailable — timer still runs, just not across page loads */
    }
    return fresh;
  }

  tick() {
    const remaining = this.deadline - Date.now();

    if (remaining <= 0) {
      this.expire();
      return;
    }

    if (!this.output) return;

    const totalSeconds = Math.floor(remaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    this.output.textContent =
      String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0');
    this.output.setAttribute('datetime', 'PT' + minutes + 'M' + seconds + 'S');
  }

  expire() {
    clearInterval(this.interval);
    this.interval = null;
    this.classList.add('is-expired');

    if (this.output) this.output.textContent = '';
    if (this.textEl && this.expiredText) this.textEl.textContent = this.expiredText;

    this.dispatchEvent(
      new CustomEvent('cartcountdown:expired', { bubbles: true })
    );
  }
}

if (!customElements.get('cart-countdown')) {
  customElements.define('cart-countdown', CartCountdown);
}